import React from "react";

import ResponsiveTable from "../../../containers/responsive-table";
import Row from "../../../containers/responsive-table/row";
import TBody from "../../../containers/responsive-table/tbody";
import HeadCell from "../../../containers/responsive-table/head-cell";
import BodyCell from "../../../containers/responsive-table/body-cell";
import { decimalsRound } from "../../../services/utils";
import Thead from "../../../containers/responsive-table/thead";
import styled from 'styled-components';


const TableWrapper = styled.div`
  padding: 20px;

  ${p => p.padding && `
    padding: ${p.padding};
  `}
`;

const TableHeaderText = styled.div`
  font-family: 'Mukta Vaani';
  font-style: normal;
  font-weight: 500;
  font-size: 18px;
  line-height: 24px;
  color: rgba(0, 27, 59, 0.94);
`;

const FinancialTargetsAndPerformance = props => {
  const {
    financialTargets,
    teamReportMetrics,
    hideTitle,
    title = 'Financial Metrics & Targets',
    padding,
    onHelpIconClick
  } = props;

  // Check which metrics are active
  const itemHasMarketShare = parseFloat(financialTargets.market_share) > 0;
  const itemHasRevenues = parseFloat(financialTargets.net_revenues) > 0;
  const itemHasGrossMargins = parseFloat(financialTargets.gross_margin) > 0;
  const itemHasSharePrice = parseFloat(financialTargets.stock_price) > 0;
  const itemHasROE = parseFloat(financialTargets.roe) > 0;
  const itemHasROA = parseFloat(financialTargets.roa) > 0;
  const itemHasEPS = parseFloat(financialTargets.earnings_per_share) > 0;

  return (
    <TableWrapper padding={padding}>
      {!hideTitle && <TableHeaderText>{title}</TableHeaderText>}
      <ResponsiveTable>
        <Thead>
          <Row>
            <HeadCell iconType="sort" cellSize="small">

            </HeadCell>
            {itemHasMarketShare && (
              <HeadCell iconType="help" onHelpIconClick={() => onHelpIconClick("Market Share")}>
                Market Share
              </HeadCell>
            )}
            {itemHasRevenues && (
              <HeadCell iconType="help" onHelpIconClick={() => onHelpIconClick("Revenues")}>
                Revenues
              </HeadCell>
            )}
            {itemHasGrossMargins && (
              <HeadCell iconType="help" onHelpIconClick={() => onHelpIconClick("Gross Margins")}>
                Gross Margins
              </HeadCell>
            )}
            {itemHasSharePrice && (
              <HeadCell iconType="help" onHelpIconClick={() => onHelpIconClick("Share Price")}>
                Share Price
              </HeadCell>
            )}
            {itemHasROE && (
              <HeadCell iconType="help" onHelpIconClick={() => onHelpIconClick("ROE")}>
                ROE
              </HeadCell>
            )}
            {itemHasROA && (
              <HeadCell iconType="help" onHelpIconClick={() => onHelpIconClick("ROA")}>
                ROA
              </HeadCell>
            )}
            {itemHasEPS && (
              <HeadCell iconType="help" onHelpIconClick={() => onHelpIconClick("EPS")}>
                EPS
              </HeadCell>
            )}
          </Row>
        </Thead>
        <TBody>
          <Row isBody>
            <BodyCell
              alignText="center"
              cellSize="small"
              backgroundVariant="default"
            >
              Target Goals
            </BodyCell>
            {itemHasMarketShare && (
              <BodyCell backgroundVariant="default">
                {financialTargets.market_share.toLocaleString('en-US', {
                  style: 'percent',
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </BodyCell>
            )}
            {itemHasRevenues && (
              <BodyCell backgroundVariant="default">
                {`$${decimalsRound(financialTargets.net_revenues / 1000000, 2)}M`}
              </BodyCell>
            )}
            {itemHasGrossMargins && (
              <BodyCell backgroundVariant="default">
                {financialTargets.gross_margin.toLocaleString('en-US', {
                  style: 'percent',
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </BodyCell>
            )}
            {itemHasSharePrice && (
              <BodyCell backgroundVariant="default">
                {financialTargets.stock_price.toLocaleString('en-US', {
                  style: 'currency',
                  currency: 'USD',
                  maximumFractionDigits: 2,
                })}
              </BodyCell>
            )}
            {itemHasROE && (
              <BodyCell backgroundVariant="default">
                {financialTargets.roe.toLocaleString('en-US', {
                  style: 'percent',
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </BodyCell>
            )}
            {itemHasROA && (
              <BodyCell backgroundVariant="default">
                {financialTargets.roa.toLocaleString('en-US', {
                  style: 'percent',
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </BodyCell>
            )}
            {itemHasEPS && (
              <BodyCell backgroundVariant="default">
                {financialTargets.earnings_per_share.toLocaleString('en-US', {
                  style: 'currency',
                  currency: 'USD',
                  maximumFractionDigits: 2,
                })}
              </BodyCell>
            )}
          </Row>
          <Row isBody>
            <BodyCell
              alignText="center"
              cellSize="small"
              backgroundVariant="default"
            >
              Team Performance
            </BodyCell>
            {itemHasMarketShare && (
              <BodyCell backgroundVariant="default">
                {teamReportMetrics['Market Share'].toLocaleString('en-US', {
                  style: 'percent',
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </BodyCell>
            )}
            {itemHasRevenues && (
              <BodyCell backgroundVariant="default">
                {`$${decimalsRound(teamReportMetrics['Net Revenues'] / 1000000, 2)}M`}
              </BodyCell>
            )}
            {itemHasGrossMargins && (
              <BodyCell backgroundVariant="default">
                {teamReportMetrics['Gross Margin'].toLocaleString('en-US', {
                  style: 'percent',
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </BodyCell>
            )}
            {itemHasSharePrice && (
              <BodyCell backgroundVariant="default">
                {teamReportMetrics['Share Price'].toLocaleString('en-US', {
                  style: 'currency',
                  currency: 'USD',
                  maximumFractionDigits: 2,
                })}
              </BodyCell>
            )}
            {itemHasROE && (
              <BodyCell backgroundVariant="default">
                {teamReportMetrics['ROE'].toLocaleString('en-US', {
                  style: 'percent',
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </BodyCell>
            )}
            {itemHasROA && (
              <BodyCell backgroundVariant="default">
                {teamReportMetrics['ROA'].toLocaleString('en-US', {
                  style: 'percent',
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </BodyCell>
            )}
            {itemHasEPS && (
              <BodyCell backgroundVariant="default">
                {teamReportMetrics['Earnings Per Share'].toLocaleString('en-US', {
                  style: 'currency',
                  currency: 'USD',
                  maximumFractionDigits: 2,
                })}
              </BodyCell>
            )}
          </Row>
        </TBody>
      </ResponsiveTable>
    </TableWrapper>
  );
};

export default FinancialTargetsAndPerformance;



// WEBPACK FOOTER //
// ./src/components/performance-feedback/components/FinancialTargetsAndPerformance.js